import React from "react";
import {CloseBtn, ModalBox, ModalTitle} from "views/components/modals/common/common.component";
import {ReactComponent as IconClose} from "assets/img/icon/close.svg";
import {Box, Modal, Tab} from "@mui/material";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {ModalType} from "data/enums";
import {eq} from "lodash";
import styled from "@emotion/styled";
import type {IHowToScoreModalController} from "views/components/modals/how_to_score/how_to_score.controller";
import topLine from "assets/img/top_line_desktop.png";
import bottomLine from "assets/img/bottom_line_desktop.png";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import {media} from "assets/css/media";

const ScoreModalBox = styled(ModalBox)`
	//min-height: 320px;
	background: url(${topLine}) no-repeat right top, url(${bottomLine}) no-repeat left bottom,
		linear-gradient(204deg, #0041f9 0%, #0041f9 45%, #ff0019 75%, #ff0019 100%);
	background-size: 20%, 15%, cover;
	border: none;
	padding: 0;

	svg {
		path {
			fill: #fff !important;
		}
	}

	.tab_panel {
		padding: 0;
	}
`;
const ScoreModalContent = styled(Box)`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	flex: 1;
`;
const ScoreModalHeader = styled(Box)`
	padding: 60px 20px 15px;
`;
const ScoreModalBody = styled(Box)`
	flex: 1 1 100%;
	background: #fff;

	.MuiTabs-flexContainer {
		border-bottom: 3px solid var(--thirdColor);

		button {
			color: var(--thirdColor);
			text-align: center;
			font-family: var(--fontFamilyBase);
			font-size: 16px;
			font-style: normal;
			font-weight: 500;
			line-height: 100%; /* 16px */
			letter-spacing: 3.2px;
			text-transform: uppercase;
			transition: all ease 0.4s;
			flex: 1;

			@media (max-width: ${media.mobile}) {
				font-size: 12px;
				letter-spacing: 0;
				min-width: unset;
			}

			&.Mui-selected {
				color: #ffffff;
				background: var(--thirdColor);
			}
		}
	}

	.MuiTabs-indicator {
		height: 3px;
		background-color: var(--thirdColor);
	}

	.MuiTypography-root {
		font-family: var(--fontFamilyBase);
		font-weight: 450;
		letter-spacing: 2px;
	}
`;

const ScoreModalTitle = styled(ModalTitle)`
	color: #fff;
	text-align: center;
	font-feature-settings: "clig" off, "liga" off;
	font-family: var(--fontFamilyBase);
	font-size: 28px;
	font-style: normal;
	font-weight: 425;
	line-height: 100%; /* 28px */
	letter-spacing: 2px;
	text-transform: uppercase;
`;

const TabContent = styled(Box)`
	width: 100%;
	display: flex;
	flex-wrap: wrap;
	min-height: 182px;
`;
const Cell = styled(Box)`
	padding: 14px;
	flex: 1 1 33.3%;
	border-left: 1px solid #e5e5ea;
	display: flex;
	gap: 8px;
	flex-direction: column;
	justify-content: center;

	&.md {
		flex: 1 1 50%;
		border-top: 1px solid #e5e5ea;
	}
`;
const Value = styled(Box)`
	display: flex;
	justify-content: center;
	color: var(--thirdColor);
	text-align: center;
	font-feature-settings: "clig" off, "liga" off;
	font-family: var(--fontFamilyBase);
	font-size: 20px;
	font-style: normal;
	font-weight: 600;
	line-height: 104%; /* 20.8px */
	text-transform: uppercase;
	flex: 1 1 50%;
	//padding: 6px 0;
	align-items: flex-end;
`;
const Title = styled(Box)`
	display: flex;
	justify-content: center;
	color: var(--thirdColor);
	text-align: center;
	font-feature-settings: "clig" off, "liga" off;
	/* Body/B1 Med */
	font-family: var(--fontFamilyBase);
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 130%; /* 20.8px */
	flex: 1 1 50%;

	@media (max-width: ${media.mobile}) {
		font-size: 12px;
	}
`;

export const HowToScoreModal: React.FC = observer(() => {
	const {i18n, visibleModal, close, tabsDataArr, onChangeTab, currentTab} =
		useViewController<IHowToScoreModalController>(Bindings.HowToScoreModalController);

	const title = i18n.t("modal.how_to_score.title", "How to score points?");

	const showPrompt = eq(ModalType.SCORE_INFO, visibleModal);

	if (!showPrompt) {
		return null;
	}

	return (
		<Modal open={showPrompt}>
			<ScoreModalBox>
				<CloseBtn onClick={close}>
					<IconClose />
				</CloseBtn>
				<ScoreModalContent>
					<ScoreModalHeader>
						<ScoreModalTitle style={{marginBottom: 25, marginTop: 15, color: "#FFF"}}>
							{title}
						</ScoreModalTitle>
					</ScoreModalHeader>
					<ScoreModalBody>
						<TabContext value={currentTab}>
							<TabList
								onChange={onChangeTab}
								aria-label="Tabs"
								className="tab_navigation">
								{tabsDataArr.map((el, index) => (
									<Tab key={index} label={el.tabName} value={el.tabName} />
								))}
							</TabList>
							{tabsDataArr.map((el, index) => (
								<TabPanel key={index} value={el.tabName} className="tab_panel">
									<TabContent>
										{el.tabData.map((it, index) => {
											return (
												<Cell
													key={index}
													className={index > 2 ? "md" : "sm"}>
													<Value>{it.value}</Value>
													<Title>{it.name}</Title>
												</Cell>
											);
										})}
									</TabContent>
								</TabPanel>
							))}
						</TabContext>
					</ScoreModalBody>
				</ScoreModalContent>
			</ScoreModalBox>
		</Modal>
	);
});
